export type RenderMode = 'shader' | 'image' | 'video' | 'ripple' | 'liquid' | 'depth';

export class Renderer {
    private canvas: HTMLCanvasElement;
    private device!: GPUDevice;
    private context!: GPUCanvasContext;
    private presentationFormat!: GPUTextureFormat;
    private pipelines = new Map<string, GPURenderPipeline | GPUComputePipeline>();
    private bindGroups = new Map<string, GPUBindGroup>();
    private sampler!: GPUSampler;
    private imageUrls: string[] = [];
    private imageTexture!: GPUTexture;
    
    // New resources for the depth effect
    private depthTexture!: GPUTexture;
    private parallaxUniformBuffer!: GPUBuffer;
    private mouseState = { x: 0.5, y: 0.5 };

    constructor(canvas: HTMLCanvasElement) { this.canvas = canvas; }

    public updateMouse(x: number, y: number) {
        this.mouseState = { x, y };
    }
    
    // New method to receive depth data from the UI
    public updateDepthMap(data: Float32Array, width: number, height: number) {
        if (!this.device) return;
        if (!this.depthTexture || this.depthTexture.width !== width || this.depthTexture.height !== height) {
            this.depthTexture = this.device.createTexture({
                size: [width, height],
                format: 'r32float', // A single red channel is enough for depth
                usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
            });
        }
        this.device.queue.writeTexture(
            { texture: this.depthTexture },
            data,
            { bytesPerRow: width * 4 }, // 4 bytes per float
            [width, height]
        );
        this.createBindGroups(); // Re-create bind groups when the texture changes
    }


    public async init(): Promise<boolean> {
        if (!navigator.gpu) return false;
        const adapter = await navigator.gpu.requestAdapter();
        if (!adapter) return false;
        this.device = await adapter.requestDevice();
        this.context = this.canvas.getContext('webgpu')!;
        this.presentationFormat = navigator.gpu.getPreferredCanvasFormat();
        this.context.configure({ device: this.device, format: this.presentationFormat, alphaMode: 'premultiplied' });

        await this.fetchImageUrls();
        await this.createResources();
        await this.createPipelines();
        this.createBindGroups();
        
        return true;
    }

    private async fetchImageUrls(): Promise<void> { /* ... unchanged ... */ }
    
    public async loadRandomImage(imageUrl: string): Promise<void> {
        try {
            const response = await fetch(imageUrl);
            const imageBitmap = await createImageBitmap(await response.blob());

            if (this.imageTexture) this.imageTexture.destroy();
            this.imageTexture = this.device.createTexture({
                size: [imageBitmap.width, imageBitmap.height],
                format: 'rgba8unorm',
                usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
            });
            this.device.queue.copyExternalImageToTexture({ source: imageBitmap }, { texture: this.imageTexture }, [imageBitmap.width, imageBitmap.height]);

            if (this.pipelines.size > 0) {
                this.createBindGroups();
            }
        } catch (e) { console.error("Failed to load image:", e); }
    }

    private async createResources(): Promise<void> {
        this.sampler = this.device.createSampler({ magFilter: 'linear', minFilter: 'linear' });
        this.parallaxUniformBuffer = this.device.createBuffer({
            size: 2 * 4, // 2 floats for mouse xy
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        });
        // Load a default image to start
        await this.loadRandomImage('https://i.imgur.com/vCNL2sT.jpeg');
    }

    private async createPipelines(): Promise<void> {
        const parallaxCode = await fetch('shaders/parallax.wgsl').then(res => res.text());
        const parallaxModule = this.device.createShaderModule({ code: parallaxCode });

        this.pipelines.set('depth', this.device.createRenderPipeline({
            layout: 'auto',
            vertex: { module: parallaxModule, entryPoint: 'vs_main' },
            fragment: {
                module: parallaxModule,
                entryPoint: 'fs_main',
                targets: [{ format: this.presentationFormat }]
            },
            primitive: { topology: 'triangle-strip' as GPUPrimitiveTopology }
        }));
    }

    private createBindGroups(): void {
        if (!this.imageTexture || !this.depthTexture) return;

        this.bindGroups.set('depth', this.device.createBindGroup({
            layout: this.pipelines.get('depth')!.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: this.sampler },
                { binding: 1, resource: this.imageTexture.createView() },
                { binding: 2, resource: this.depthTexture.createView() },
                { binding: 3, resource: { buffer: this.parallaxUniformBuffer } },
            ]
        }));
    }

    public render(mode: RenderMode, videoElement: HTMLVideoElement, zoom: number, panX: number, panY: number): void {
        if (!this.device) return;

        const commandEncoder = this.device.createCommandEncoder();
        const textureView = this.context.getCurrentTexture().createView();
        const renderPassDescriptor: GPURenderPassDescriptor = {
            colorAttachments: [{ view: textureView, loadOp: 'clear' as GPULoadOp, storeOp: 'store' as GPUStoreOp, clearValue: { r: 0, g: 0, b: 0, a: 1 } }]
        };
        const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);

        if (mode === 'depth' && this.bindGroups.has('depth')) {
            this.device.queue.writeBuffer(this.parallaxUniformBuffer, 0, new Float32Array([this.mouseState.x, this.mouseState.y]));
            passEncoder.setPipeline(this.pipelines.get('depth') as GPURenderPipeline);
            passEncoder.setBindGroup(0, this.bindGroups.get('depth')!);
            passEncoder.draw(4);
        }

        passEncoder.end();
        this.device.queue.submit([commandEncoder.finish()]);
    }
}
